import { useCallback, useEffect, useState } from 'react';

import type {
  ResearchBoard,
  ResearchBoardNotification,
  ResearchBoardSyncResult,
} from './index';

type SelectedBoard = Pick<ResearchBoard, 'id' | 'title'>;
type SaveStateListener = (selectedBoards: SelectedBoard[]) => void;

const pinnedProductState = new Map<string, Promise<SelectedBoard[]>>();
const pinnedProductListeners = new Map<string, Set<SaveStateListener>>();

const getProductKey = (userId: string, productCanonicalId: string) =>
  `${userId}:${productCanonicalId}`;

const subscribeToProduct = (
  userId: string,
  productCanonicalId: string,
  listener: SaveStateListener
) => {
  const key = getProductKey(userId, productCanonicalId);
  const listeners = pinnedProductListeners.get(key) || new Set();
  listeners.add(listener);
  pinnedProductListeners.set(key, listeners);

  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      pinnedProductListeners.delete(key);
    }
  };
};

const updateProductState = (
  userId: string,
  productCanonicalId: string,
  selectedBoards: SelectedBoard[]
) => {
  const key = getProductKey(userId, productCanonicalId);
  pinnedProductState.set(key, Promise.resolve(selectedBoards));
  pinnedProductListeners
    .get(key)
    ?.forEach((listener) => listener(selectedBoards));
};

const isUnauthorizedError = (error: unknown) =>
  error instanceof Error && 'status' in error && error.status === 401;

export type UseResearchBoardSaveOptions = {
  client: {
    listBoards: (productCanonicalId: string) => Promise<ResearchBoard[]>;
    syncProduct: (args: {
      productCanonicalId: string;
      selectedBoardIds: string[];
      source?: string;
    }) => Promise<ResearchBoardSyncResult>;
  };
  productCanonicalId: string | null;
  userId?: string | null;
  isAuthenticated: boolean;
  initialIsPinned?: boolean;
  consumePickerIntent?: (productCanonicalId: string) => boolean;
  onSignIn: (productCanonicalId: string) => void | Promise<unknown>;
  onAuthenticationRequired?: () => void;
  source?: string;
};

export type UseResearchBoardSaveResult = {
  isPinned: boolean;
  drawerOpen: boolean;
  notification: ResearchBoardNotification | null;
  isRemoving: boolean;
  setDrawerOpen: (open: boolean) => void;
  setNotification: (notification: ResearchBoardNotification | null) => void;
  handleTrigger: () => void;
  handleSaved: (
    result: ResearchBoardSyncResult,
    savedBoards?: SelectedBoard[]
  ) => void;
};

export const useResearchBoardSave = ({
  client,
  productCanonicalId,
  userId,
  isAuthenticated,
  initialIsPinned = false,
  consumePickerIntent,
  onSignIn,
  onAuthenticationRequired,
  source,
}: UseResearchBoardSaveOptions): UseResearchBoardSaveResult => {
  const [isPinned, setIsPinned] = useState(initialIsPinned);
  const [selectedBoards, setSelectedBoards] = useState<SelectedBoard[] | null>(
    null
  );
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [notification, setNotification] =
    useState<ResearchBoardNotification | null>(null);

  useEffect(() => {
    setIsPinned(initialIsPinned);
    setSelectedBoards(null);
  }, [initialIsPinned]);

  useEffect(() => {
    if (
      isAuthenticated &&
      productCanonicalId &&
      consumePickerIntent?.(productCanonicalId)
    ) {
      setDrawerOpen(true);
    }
  }, [consumePickerIntent, isAuthenticated, productCanonicalId]);

  useEffect(() => {
    if (!isAuthenticated || !userId || !productCanonicalId) {
      return undefined;
    }

    let isCurrent = true;
    const productKey = getProductKey(userId, productCanonicalId);
    const unsubscribe = subscribeToProduct(
      userId,
      productCanonicalId,
      (nextSelectedBoards) => {
        setSelectedBoards(nextSelectedBoards);
        setIsPinned(nextSelectedBoards.length > 0);
      }
    );
    let request = pinnedProductState.get(productKey);

    if (!request) {
      request = client
        .listBoards(productCanonicalId)
        .then((boards) =>
          boards
            .filter((board) => board.selected)
            .map(({ id, title }) => ({ id, title }))
        )
        .catch((error) => {
          pinnedProductState.delete(productKey);
          throw error;
        });
      pinnedProductState.set(productKey, request);
    }

    // This is only a background membership lookup. If the user has the
    // picker open, its own request owns the reauthentication flow; firing
    // here as well can race the drawer or start multiple redirects when a
    // page contains several save buttons.
    request
      .then((nextSelectedBoards) => {
        if (isCurrent && pinnedProductState.get(productKey) === request) {
          setSelectedBoards(nextSelectedBoards);
          setIsPinned(nextSelectedBoards.length > 0);
        }
      })
      .catch((): undefined => undefined);

    return () => {
      isCurrent = false;
      unsubscribe();
    };
  }, [client, isAuthenticated, productCanonicalId, userId]);

  const handleSignIn = useCallback(() => {
    if (!productCanonicalId) return;

    try {
      const signInResult = onSignIn(productCanonicalId);
      if (signInResult && typeof signInResult.then === 'function') {
        void signInResult.catch(() => {
          setNotification({
            message: 'We could not start G2 sign-in. Please try again.',
            type: 'error',
          });
        });
      }
    } catch {
      setNotification({
        message: 'We could not start G2 sign-in. Please try again.',
        type: 'error',
      });
    }
  }, [onSignIn, productCanonicalId]);

  const handleSaved = useCallback(
    (result: ResearchBoardSyncResult, savedBoards?: SelectedBoard[]) => {
      setIsPinned(result.pinned);
      const selectedBoardById = new Map(
        [...(selectedBoards || []), ...(savedBoards || [])].map((board) => [
          board.id,
          board,
        ])
      );
      const nextSelectedBoards = result.selectedBoardIds.map((id) => ({
        id,
        title: selectedBoardById.get(id)?.title || '',
      }));
      setSelectedBoards(nextSelectedBoards);
      if (userId && productCanonicalId) {
        updateProductState(userId, productCanonicalId, nextSelectedBoards);
      }
    },
    [productCanonicalId, selectedBoards, userId]
  );

  const handleTrigger = useCallback(() => {
    if (!productCanonicalId) return;

    if (!isAuthenticated) {
      handleSignIn();
      return;
    }

    // A product saved to exactly one board can be removed without opening the
    // picker. Multiple memberships still need the picker so the user can
    // choose which boards to keep. Unknown board state deliberately falls
    // back to the picker rather than risking an unintended removal.
    if (selectedBoards?.length === 1 && selectedBoards[0].title) {
      const [selectedBoard] = selectedBoards;
      setIsRemoving(true);
      void client
        .syncProduct({
          productCanonicalId,
          selectedBoardIds: [],
          source,
        })
        .then((result) => {
          handleSaved(result);
          setNotification({
            message: `Removed from ${selectedBoard.title}`,
            type: 'success',
          });
        })
        .catch((error) => {
          if (isUnauthorizedError(error)) {
            onAuthenticationRequired?.();
          } else {
            setNotification({
              message:
                'We could not update your Saved Products. Please try again.',
              type: 'error',
            });
          }
        })
        .finally(() => setIsRemoving(false));
      return;
    }

    setDrawerOpen(true);
  }, [
    client,
    handleSaved,
    handleSignIn,
    isAuthenticated,
    onAuthenticationRequired,
    productCanonicalId,
    selectedBoards,
    source,
  ]);

  return {
    drawerOpen,
    handleSaved,
    handleTrigger,
    isPinned,
    isRemoving,
    notification,
    setDrawerOpen,
    setNotification,
  };
};
